/* ══════════════════════════════════════════════════════════════════
   [D-548] «📲 Переходи в APP» — сторінка в картці партнера.

   OWNER 12.10: «зроби в дашборді окрему сторінку у партнера щоб я бачив хто переходив з його учнів чи він сам у мої застосунки і
   скільки разів». Згори — застосунки картками (скільки разів відкривали, скільки дивились презентацію, скільки людей); нижче —
   люди картками в сітці: учень чи сам партнер, і його переходи по кожному застосунку. Період — кнопками.
   ══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import api from '../api';
import { srcLine } from './appClickSrc';   // [D-768] «📍 Звідки»

const C = { card2: '#1b2836', line: 'rgba(0,229,160,.22)', txt: '#eaf6ff', dim: '#a8bece', neon: '#00e5a0', warn: '#ffb74d', blue: '#4fc3f7' };
const grid = (min) => ({ display: 'grid', gap: 1.2, gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` });
const fdt = (v) => (v ? new Date(v).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const PERIODS = [[7, '7 днів'], [30, '30 днів'], [90, '90 днів'], [0, 'увесь час']];
// 1 раз · 2-4 рази · 5+ разів
const wTimes = (n) => {
  const a = n % 10, b = n % 100;
  return (a === 1 && b !== 11) ? 'раз' : ((a >= 2 && a <= 4 && (b < 12 || b > 14)) ? 'рази' : 'разів');
};

export default function PartnerAppClicks({ p }) {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let dead = false; setD(null); setErr('');
    api.get(`/partners/${p.id}/app-clicks`, { params: { days } }).then(r => { if (!dead) setD(r.data); })
      .catch(e => { if (!dead) setErr((e && e.response && e.response.data && e.response.data.detail) || 'Не вдалося завантажити'); });
    return () => { dead = true; };
  }, [p.id, days]);
  const title = (k) => { const a = ((d && d.apps) || []).find(x => x.key === k); return a ? (a.icon || '📱') + ' ' + a.title : k; };
  return (
    <Box data-ac="page">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.4 }}>
        <Typography sx={{ color: C.txt, fontWeight: 800, fontSize: 15, flex: 1, minWidth: 200 }}>📲 Хто з учнів партнера (і він сам) переходив у ваші застосунки</Typography>
        {PERIODS.map(([n, t]) => (
          <Button key={n} size="small" data-ac-days={n} onClick={() => setDays(n)}
                  sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, minWidth: 0, px: 1.2, borderRadius: 2,
                        border: `1px solid ${days === n ? C.neon : 'rgba(255,255,255,.14)'}`, color: days === n ? '#04121a' : C.txt, bgcolor: days === n ? C.neon : 'transparent',
                        '&:hover': { bgcolor: days === n ? C.neon : 'rgba(255,255,255,.06)' } }}>{t}</Button>
        ))}
      </Box>
      {err && <Typography sx={{ color: '#ff8a80', fontSize: 13 }}>{err}</Typography>}
      {!d && !err && <Typography sx={{ color: C.dim, fontSize: 13 }}>Завантаження…</Typography>}
      {d && (<>
        <Typography data-ac="sum" sx={{ color: C.dim, fontSize: 12.5, mb: 1 }}>
          Усього переходів: <b style={{ color: C.txt }}>{d.total}</b> · людей: <b style={{ color: C.txt }}>{d.people.length}</b> · учнів у партнера: {d.students}
        </Typography>
        {d.total === 0 && <Typography data-ac="empty" sx={{ color: C.dim, fontSize: 13.5, py: 2 }}>За цей період ніхто з учнів цього партнера у ваші застосунки не переходив.</Typography>}
        {d.total > 0 && (<>
          <Box sx={{ ...grid(190), mb: 1.8 }}>
            {d.apps.filter(a => a.opens + a.previews > 0).map(a => (
              <Box key={a.key} data-ac-app={a.key} sx={{ bgcolor: C.card2, borderRadius: 2.5, p: 1.4, borderTop: `3px solid ${C.neon}` }}>
                <Typography sx={{ color: C.txt, fontWeight: 800, fontSize: 14 }}>{a.icon || '📱'} {a.title}</Typography>
                <Typography sx={{ color: C.neon, fontWeight: 900, fontSize: 22, lineHeight: 1.2 }}>{a.opens}<span style={{ fontSize: 12, color: C.dim, fontWeight: 600 }}> {wTimes(a.opens)} відкрили</span></Typography>
                <Typography sx={{ color: C.dim, fontSize: 12.5 }}>презентацію дивились: {a.previews} · людей: {a.users}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={grid(250)}>
            {d.people.map(u => (
              <Box key={u.tg} data-ac-user={u.tg} sx={{ bgcolor: C.card2, borderRadius: 2.5, p: 1.4, borderTop: `3px solid ${u.role === 'partner' ? C.warn : C.blue}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .8 }}>
                  <Typography sx={{ color: C.txt, fontWeight: 800, fontSize: 14, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.username ? '@' + u.username : 'id ' + u.tg}</Typography>
                  <Box sx={{ px: .8, py: .1, borderRadius: 1, bgcolor: u.role === 'partner' ? C.warn : C.blue }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#06120a' }}>{u.role === 'partner' ? '🧑‍💼 сам партнер' : '👤 учень'}</Typography></Box>
                </Box>
                <Typography sx={{ color: C.dim, fontSize: 12 }}>id {u.tg} · востаннє {fdt(u.last)}</Typography>
                {srcLine(u.srcs) ? <Typography data-ac-src={u.tg} sx={{ color: C.dim, fontSize: 11.5 }}>📍 {srcLine(u.srcs)}</Typography> : null}
                <Typography sx={{ color: C.txt, fontSize: 13, fontWeight: 700, mt: .4 }}>переходів: {u.total}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .6, mt: .6 }}>
                  {Object.keys(u.by).map(k => (
                    <Box key={k} sx={{ px: .9, py: .3, borderRadius: 1.5, border: '1px solid rgba(255,255,255,.14)' }}>
                      <Typography sx={{ fontSize: 12, color: C.txt }}>{title(k)}{u.by[k].open ? <b style={{ color: C.neon }}> ×{u.by[k].open}</b> : null}{u.by[k].preview ? <span style={{ color: C.dim }}> · 👁 {u.by[k].preview}</span> : null}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: C.dim, fontSize: 11.5, mt: 1.2 }}>×N — скільки разів відкрив застосунок; 👁 — скільки разів відкрив його презентацію (картка з оком); 📍 — звідки перейшов (до 30.10 не записували). Ті самі події приходять у гілку «👆 Переходи в застосунки» вашої групи.</Typography>
        </>)}
      </>)}
    </Box>
  );
}
